import { BALANCE } from './balance';
import { ITEMS, item } from './items';
import type { CarrySlotState, ItemId, PlayerState, StoreId } from './types';

/**
 * CARRYING THINGS.
 *
 * Two separate constraints, deliberately:
 *
 *  - Loot and consumables share eight slots and a 45kg budget. Weight slows you
 *    down continuously, so the decision "is this worth the speed" is live on
 *    every single pickup.
 *  - Tools get their own two slots and are exempt from the slot count but NOT
 *    from the weight. A grinder and a drill together are 15.6kg before you have
 *    stolen anything, which is the price of being able to open the good stores.
 */

export type PickupRefusal = 'slots-full' | 'too-heavy' | 'tool-slots-full' | 'unknown-item';

export interface PickupResult {
  ok: boolean;
  reason?: PickupRefusal;
}

export function carriedWeight(p: PlayerState): number {
  let kg = 0;
  for (const slot of p.carried) kg += ITEMS[slot.item]?.weight ?? 0;
  for (const t of p.tools) kg += ITEMS[t]?.weight ?? 0;
  return round3(kg);
}

export function carriedValue(p: PlayerState): number {
  let v = 0;
  for (const slot of p.carried) v += ITEMS[slot.item]?.value ?? 0;
  return v;
}

/**
 * Effective weight after consumables. Painkillers halve what your legs believe
 * they are carrying; they do not change what the drop-off pays out.
 */
export function effectiveWeight(p: PlayerState): number {
  const raw = carriedWeight(p);
  let mul = 1;
  for (const e of p.effects) {
    if (e.effect.kind === 'strength' && e.remainingSec > 0) mul *= e.effect.magnitude;
  }
  return round3(raw * mul);
}

export interface Encumbrance {
  kg: number;
  /** Fraction of capacity, 0..1+. */
  load: number;
  tier: string;
  /** Multiplier on every movement speed. */
  speed: number;
  /** Multiplier on jump velocity. */
  jump: number;
}

/**
 * Interpolates the tier table rather than stepping it, so there is no threshold
 * a player can hover exactly beneath. The tier NAME still steps, because the HUD
 * needs to say something, and "STAGGERING" is more useful than "0.71x".
 */
export function encumbrance(p: PlayerState): Encumbrance {
  const kg = effectiveWeight(p);
  const tiers = BALANCE.carry.tiers;
  const cap = BALANCE.carry.capacityKg;

  let speed = tiers[tiers.length - 1].speed;
  let tier = tiers[0].name as string;

  for (let i = 0; i < tiers.length; i++) {
    const lo = tiers[i];
    const hi = tiers[i + 1];
    if (!hi) {
      speed = lo.speed;
      tier = lo.name;
      break;
    }
    if (kg <= hi.kg) {
      const t = hi.kg === lo.kg ? 0 : (kg - lo.kg) / (hi.kg - lo.kg);
      speed = lo.speed + (hi.speed - lo.speed) * clamp01(t);
      // The name flips at the halfway point of the band, so it lags the number
      // slightly and reads as a warning rather than a readout.
      tier = t > 0.5 ? hi.name : lo.name;
      break;
    }
  }

  const load = kg / cap;
  const jump = 1 + (BALANCE.carry.jumpAtCapacity - 1) * clamp01(load);
  return { kg, load, tier, speed: round3(speed), jump: round3(jump) };
}

export function canPickUp(p: PlayerState, id: ItemId): PickupResult {
  const def = ITEMS[id];
  if (!def) return { ok: false, reason: 'unknown-item' };

  if (def.kind === 'tool') {
    if (p.tools.length >= BALANCE.carry.toolSlots) return { ok: false, reason: 'tool-slots-full' };
  } else if (p.carried.length >= BALANCE.carry.itemSlots) {
    return { ok: false, reason: 'slots-full' };
  }

  if (carriedWeight(p) + def.weight > BALANCE.carry.capacityKg) {
    return { ok: false, reason: 'too-heavy' };
  }
  return { ok: true };
}

/** Mutates the player. Returns whether it happened and why not. */
export function pickUp(p: PlayerState, id: ItemId, from: StoreId | null = null): PickupResult {
  const check = canPickUp(p, id);
  if (!check.ok) return check;
  if (item(id).kind === 'tool') p.tools.push(id);
  else p.carried.push({ item: id, store: from });
  return { ok: true };
}

/** Removes one instance. Returns the slot, or null if it was not being carried. */
export function drop(p: PlayerState, id: ItemId): CarrySlotState | null {
  const ti = p.tools.indexOf(id);
  if (ti >= 0) {
    p.tools.splice(ti, 1);
    return { item: id, store: null };
  }
  const ci = p.carried.findIndex((s) => s.item === id);
  if (ci < 0) return null;
  return p.carried.splice(ci, 1)[0];
}

/** Everything a caught thief leaves on the floor. Tools included — they are loot too. */
export function dropAll(p: PlayerState): CarrySlotState[] {
  const spill = [...p.carried, ...p.tools.map((t) => ({ item: t, store: null }))];
  p.carried = [];
  p.tools = [];
  return spill;
}

export function hasTool(p: PlayerState, id: ItemId): boolean {
  return p.tools.includes(id);
}

/** Advances consumable timers. Called once per sim tick. */
export function tickEffects(p: PlayerState, dt: number): void {
  for (const e of p.effects) e.remainingSec -= dt;
  p.effects = p.effects.filter((e) => e.remainingSec > 0);
}

/** Consumes one and applies its effect. Re-using the same kind refreshes the timer. */
export function useConsumable(p: PlayerState, id: ItemId): boolean {
  const def = ITEMS[id];
  if (!def || def.kind !== 'consumable' || !def.effect) return false;
  if (!p.carried.some((s) => s.item === id)) return false;
  drop(p, id);
  const existing = p.effects.find((e) => e.effect.kind === def.effect!.kind);
  if (existing) existing.remainingSec = Math.max(existing.remainingSec, def.effect.durationSec);
  else p.effects.push({ effect: def.effect, remainingSec: def.effect.durationSec });
  return true;
}

/** Combined speed multiplier from encumbrance and any speed consumable. */
export function speedMultiplier(p: PlayerState): number {
  let mul = encumbrance(p).speed;
  for (const e of p.effects) {
    if (e.effect.kind === 'speed' && e.remainingSec > 0) mul *= e.effect.magnitude;
  }
  return round3(mul);
}

/** Noise this player makes for a given base radius, after any silence effect. */
export function noiseRadius(p: PlayerState, base: number): number {
  for (const e of p.effects) {
    if (e.effect.kind === 'silence' && e.remainingSec > 0) return round3(base * e.effect.magnitude);
  }
  return base;
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);
const round3 = (n: number) => Math.round(n * 1000) / 1000;
